import { createTheme } from '@mui/material/styles'
import {
  darkLightError,
  InfoBackground,
  lightError,
  white,
} from './constants/colors'

// Define any styles expected to be used in multiple places in the theme here
const textDisabledColor = 'rgba(0, 0, 0)' //Disabled color is black, not grey
const textSecondaryColor = 'rgba(0, 0, 0, 0.6)'
const textPrimaryColor = 'rgba(0, 0, 0)'
const contrastTextColor = '#fff'
const textFontSize = 14

const boxShadow =
  '0px 5px 5px -3px rgba(0,0,0,0.1), 0px 8px 10px 1px rgba(0,0,0,0.05), 0px 3px 14px 2px rgba(0,0,0,0.06)'

// Colours currently used in the AngularJS styles
const brandColours = {
  darkBlue0: '#122735',
  darkBlue1: '#263d51',
  actionBlue: '#2c5ed7',
  latte1: '#eddbb7',
  latte2: '#eeebd4',
  latte3: '#faf9f2',
  inputBackground: '#f5f5f5',
  inputBorder: '#c4c4c4',
  errorPrimary: '#ff0303',
  successPrimary: '#45ad06',
  disabledPrimary: '#606060',
}

// Define custom theme variables for Loaded specific usage
declare module '@mui/material/styles' {
  interface Theme {
    loaded: {
      /** The space taken up by the app header, this is currently set to match what AngularJS renders. */
      pageHeaderHeight: number
      /** The gutter size used between elements, this is currently set to match what AngularJS uses. */
      gutterSize: number
      /** A standard drop shadow used to provide depth. */
      boxShadow: string
    }
    app: {
      pageBackground: string
      headerBackground: string
      headerTextColor: string
      sidenavBackground: string
      sidenavSubnavBackground: string
      sidenavTextColor: string
      /** The width of the sidenav in css units, this is currently set to match what AngularJS uses. */
      sidenavWidth: string
    }
  }
  interface ThemeOptions {
    loaded: {
      pageHeaderHeight: number
      gutterSize: number
      boxShadow: string
    }
    app: {
      pageBackground: string
      headerBackground: string
      headerTextColor: string
      sidenavBackground: string
      sidenavSubnavBackground: string
      sidenavTextColor: string
      sidenavWidth: string
    }
  }
  interface BreakpointOverrides {
    xs: true
    sm: true
    smxl: true // custom breakpoint
    md: true
    lg: true
    xl: true
  }
  interface Palette {
    cancel: Palette['primary']
    deactivate: Palette['primary']
    loadedAction: {
      text: string
      background: string
    }
    inputBorder: {
      main: string
    }
  }
  interface PaletteOptions {
    cancel?: PaletteOptions['primary']
    deactivate?: PaletteOptions['primary']
    loadedAction?: {
      text: string
      background: string
    }
    inputBorder?: {
      main: string
    }
  }
}

//Declaring module is needed in order to be able to add new variants without typescript errors
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    deactivate: true
    outlinedDeactivate: true
  }
  interface ButtonPropsColorOverrides {
    deactivate: true
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    deactivate: true
  }
}

// Add custom Typography variant for ellipsis

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    ellipsis: true
  }
}

// Set up some customisation for MUI components that can be rendered by React
const theme = createTheme({
  loaded: {
    pageHeaderHeight: 80,
    gutterSize: 20,
    boxShadow,
  },
  app: {
    pageBackground: brandColours.latte3,
    headerBackground: brandColours.darkBlue1,
    headerTextColor: brandColours.latte1,
    sidenavBackground: brandColours.latte2,
    sidenavSubnavBackground: brandColours.latte2,
    sidenavTextColor: brandColours.darkBlue1,
    sidenavWidth: '230px',
  },
  breakpoints: {
    // The breakpoint values are based on the most used screen sizes in loadedhub.com website
    values: {
      xs: 0,
      sm: 600,
      smxl: 900,
      md: 1150,
      lg: 1536,
      xl: 1920,
    },
  },
  palette: {
    mode: 'light',
    text: {
      disabled: textDisabledColor,
      secondary: textSecondaryColor,
      primary: textPrimaryColor,
    },
    loadedAction: {
      text: brandColours.actionBlue,
      background: InfoBackground,
    },
    // Deprecated: use loadedAction for new designs
    primary: {
      main: brandColours.actionBlue,
      contrastText: contrastTextColor,
    },
    secondary: {
      main: brandColours.latte1,
    },
    info: {
      main: brandColours.actionBlue,
      contrastText: contrastTextColor,
    },
    success: {
      main: brandColours.successPrimary,
      contrastText: contrastTextColor,
    },
    error: {
      main: brandColours.errorPrimary,
    },
    cancel: {
      main: brandColours.disabledPrimary,
    },
    deactivate: {
      main: lightError,
      dark: darkLightError,
      contrastText: white,
    },
    inputBorder: {
      main: brandColours.inputBorder,
    },
  },
  typography: {
    // Tell MUI what's the font-size on the html element is, this is required
    // as Bootstrap is setting it to 10px currently which is unexpected by MUI
    // and causes everything calculated with REM to be very small. MUI would
    // normally work with a 16px root font size - the default in browsers.
    htmlFontSize: 10,
    fontSize: textFontSize,
    fontFamily: 'Avenir',
    button: {
      textTransform: 'none',
    },
    h1: {
      fontSize: '3rem',
      fontWeight: 400,
      lineHeight: '116.7%',
    },
    h2: {
      fontSize: '2.4rem',
      fontWeight: 300,
      lineHeight: '120%',
      letterSpacing: '-0.05rem',
    },
    h3: {
      fontSize: '2.4rem',
      fontWeight: 400,
      lineHeight: '116.7%',
    },
    h4: {
      fontSize: '2rem',
      fontWeight: 400,
      lineHeight: '123.5%',
      letterSpacing: '0.025rem',
    },
    h5: {
      fontSize: '1.4rem',
      fontWeight: 700,
      lineHeight: '133.4%',
    },
    h6: {
      fontSize: '2rem',
      fontWeight: 500,
      lineHeight: '160%',
      letterSpacing: '0.015rem',
    },
    caption: {
      fontSize: '1.2rem',
      fontWeight: 400,
      lineHeight: '166%',
      letterSpacing: '0.04rem',
    },
    overline: {
      fontSize: '1.2rem',
      fontWeight: 400,
      lineHeight: '266%',
      letterSpacing: '0.1rem',
      textTransform: 'uppercase',
    },
    subtitle2: {
      fontSize: '1.2rem',
      fontWeight: 400,
      lineHeight: '157%',
      letterSpacing: '0.01rem',
    },
    subtitle1: {
      fontSize: '1.6rem',
      fontWeight: 400,
      lineHeight: '175%',
      letterSpacing: '0.015rem',
    },
    body2: {
      fontSize: '1.4rem',
      fontWeight: 400,
      lineHeight: '143%',
      letterSpacing: '0.017rem',
    },
    body1: {
      fontSize: '1.4rem',
      fontWeight: 400,
      lineHeight: '150%',
      letterSpacing: '0.015rem',
    },
  },
  components: {
    //Many of the theme customisations are in place to override the AngularJS
    //apps styling rules that otherwise win due to their selectivity being
    //higher than the MUI styles. Hopefully in the not too distant future we
    //are in a place where the app shell is rendered by React and these
    //overrides can be removed.
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
        }),
      },
    },
    MuiStack: {
      defaultProps: {
        spacing: '10px',
      },
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: 'ellipsis' },
          style: {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            display: 'block',
          },
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          '&:focus': {
            outline: 'none',
          },
          ...(ownerState.variant === 'text' && {
            '&:hover': {
              background: 'none',
            },
          }),
          // Prevent hash-based routing URLs from displaying in print media for buttons
          // rendered as anchor tags (e.g., LoadedLink with layout="button").
          '@media print': {
            '&[href*="#/"]:after': {
              content: '""',
            },
          },
        }),
      },
      variants: [
        {
          props: {
            variant: 'deactivate',
          },
          style: {
            color: white,
            backgroundColor: lightError,
            '&:hover': {
              backgroundColor: darkLightError,
            },
          },
        },
        {
          props: {
            variant: 'outlinedDeactivate',
          },
          style: {
            color: lightError,
            borderColor: lightError,
            border: '1px solid',
            '&:hover': {
              borderColor: darkLightError,
              color: darkLightError,
              backgroundColor: 'transparent',
            },
          },
        },
      ],
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          '.MuiInputBase-root': {
            ...(!ownerState.fullWidth && {
              width: '15rem',
            }),

            ...(!ownerState.multiline && {
              height: '3.2rem',
            }),
            paddingLeft: '1.2rem',
            paddingRight: '1.2rem',
            paddingTop: '0.8rem',
            paddingBottom: '0.8rem',
            alignContent: 'center',
          },

          '.MuiInputBase-input': {
            border: '0px !important',
            lineHeight: '2.4rem !important',
            letterSpacing: '0.015rem !important',
            fontWeight: '400 !important',
            padding: '0px !important',
            fontSize: `${textFontSize}px`,
            color: `${textPrimaryColor}`,
            boxShadow: 'none !important',
          },
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          padding: theme.spacing(0),
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          '& .MuiInputBase-root': {
            ...(ownerState.fullWidth == false && {
              width: '15rem',
            }),

            ...(!ownerState.multiline
              ? {
                  height: '3.2rem',
                }
              : {
                  height: '10rem !important',
                }),
            paddingLeft: '1.2rem',
            paddingRight: '1.2rem',
            paddingTop: '0.8rem',
            paddingBottom: '0.8rem',
          },
          '& .MuiInputBase-input': {
            border: '0px !important',
            letterSpacing: '0.015rem !important',
            fontWeight: '400 !important',
            padding: '0px !important',
            fontSize: `${textFontSize}px`,
            color: `${textPrimaryColor}`,
            boxShadow: 'none !important',
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: brandColours.inputBorder,
          alignSelf: 'stretch',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          '&.MuiInputBase-root': {
            ...(!ownerState.fullWidth && {
              width: '15rem',
            }),

            ...(!ownerState.multiline && {
              height: '3.2rem',
            }),
            paddingLeft: '1.2rem !important',
            paddingTop: '0.8rem !important',
            paddingBottom: '0.8rem !important',
          },
          '& .MuiInputBase-input': {
            border: '0px !important',
            lineHeight: '2.4rem !important',
            letterSpacing: '0.015rem !important',
            fontWeight: '400 !important',
            padding: '0px !important',
            fontSize: `${textFontSize}px !important`,
            color: `${textPrimaryColor} !important`,
            boxShadow: 'none !important',
          },
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          display: 'flex',
          alignItems: 'center',
          marginRight: '0 !important',
          marginBottom: '0 !important',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: '0px',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginRight: '0 !important',
          marginBottom: '0 !important',
        },
        label: {
          color: textSecondaryColor,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        selectLabel: {
          margin: '0 !important',
        },
        displayedRows: {
          margin: '0 !important',
        },
      },
    },
    MuiScopedCssBaseline: {
      styleOverrides: {
        root: {
          // Make the backgound match what we have defined in AngularJS, if not
          // we end up with the background around margins etc being white which
          // appears as if no margins exist against the page background.
          backgroundColor: brandColours.inputBackground,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          // Prevent hash-based routing URLs from displaying in print media.
          // LoadedLink components generate hrefs like "/ui/time#/route" for internal
          // navigation to support right-click "Open in new tab" (LOADED-7931).
          // Without this, Bootstrap's print CSS appends URLs after link text
          // (e.g., "Staff Name (/ui/time#/staff/123)"), breaking table layouts.
          '@media print': {
            '&[href*="#/"]:after': {
              content: '""',
            },
          },
        },
      },
    },
    // These style overrides ensure all dialogs have consistent 30px padding.
    // By default, MUI's Dialog wraps its content in a Paper component with 16px padding,
    // which we set to 0 to give full control to the inner components.
    // DialogContent, DialogTitle, and DialogActions are each explicitly set to 30px padding
    // to ensure visual consistency across the entire modal layout.
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '30px', // override default 20px 24px
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          padding: 0, // override default 16px
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '30px', // overrides default 16px 24px
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          // Set top padding to 0px on the dialog actions to avoid the 60px gap
          // between the content and actions. Since actions are optional in our
          // custom dialogs, it's safer to adjust the padding here rather than
          // setting bottom padding on the dialog content.
          padding: '0px 30px 30px 30px', // override default 8px
        },
      },
    },
  },
})

export default theme
